import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate, useParams} from "react-router";
import {CircularProgress, MenuItem, TextField, Typography} from "@mui/material";
import {ArrowBack} from "@mui/icons-material";
import Axios from "../util/Axios";
import CCButton from "../components/Common/CCButton";
import css_buttons from "../components/Common/css/CCButton.module.scss";
import Palette from "../components/FormDesigner/Palette";
import Canvas from "../components/FormDesigner/Canvas";
import Inspector from "../components/FormDesigner/Inspector";
import FormRenderer from "../components/Forms/FormRenderer";
import FormLinks from "../components/FormDesigner/FormLinks";
import PageHeader from "../components/Common/PageHeader";
import {
    addDisplay, addSection, emptyDefinition, inventField, itemsIn, moveInto, placeField,
    removeDisplay, removeField, removeSection, renameSection, sectionKeyFor, unplaced,
    updateDisplay, updatePlacement, targetSectionFor, hiddenButRequiredCodes, addTab, removeTab,
    renameTab, CanvasItem,
} from "../components/FormDesigner/designerModel";
import {DesignResponse, DisplayItem, FormDefinition, Placement, Section, Selection, Shape,
        ShapeField, Tab} from "../components/FormDesigner/types";
import {Grey7, Grey17} from "../theme/styles";
import {statusPill} from "../util/statusPill";

/**
 * Designing a form: the palette, the canvas and the inspector, over one form revision.
 *
 * THE DOCUMENT IN STATE IS THE DOCUMENT THE SERVER STORES. No translation layer, because a
 * translation is where the designer starts believing something the server does not — and the server
 * is the one that decides, overwriting what a placed field IS from the shape on every save.
 *
 * SAVING IS EXPLICIT. Auto-save on a canvas means a mis-drop is committed before it is noticed, and
 * a form is a definition other people's records are seeded from. The unsaved marker is the whole
 * safety net, so it has to be honest.
 *
 * THE SHAPE IS CHOSEN ONCE AND THEN LOCKED. Changing it would invalidate every placement in one
 * click, and there is no honest way to migrate them — the codes mean different things in a different
 * shape. Start another form instead; that is what several forms per type is for.
 */

const IDOC = "v1.ObjectRevision-001";

export default function FormDesignerPage() {
    const {usid} = useParams();
    const navigate = useNavigate();

    const [design, setDesign] = useState<DesignResponse | null>(null);
    const [definition, setDefinition] = useState<FormDefinition>(emptyDefinition());
    const [shapeFields, setShapeFields] = useState<ShapeField[]>([]);
    const [shapes, setShapes] = useState<Shape[]>([]);
    const [dataTypes, setDataTypes] = useState<any[]>([]);
    const [selection, setSelection] = useState<Selection>(null);
    const [openTab, setOpenTab] = useState<string | null>(null);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    // Three views over one form: what it asks, what it looks like, and where it is offered.
    // A toggle rather than routes, because they are three readings of the same thing and switching
    // must not lose an unsaved canvas.
    const [view, setView] = useState<"design" | "preview" | "links">("design");
    // Options per field, so a visibility condition on a picker offers its VALUES rather than asking
    // somebody to paste a key. The same call the preview uses, against the shape.
    const [fieldOptions, setFieldOptions] = useState<Record<string, any[]>>({});

    const editable = !!design?.Editable;

    const load = useCallback(() => {
        setLoading(true);
        Axios({method: "GET", url: "t1/v1/form_design", params: {FormRevisionUSID: usid}})
            .then((response: any) => {
                const body: DesignResponse = response.data;
                setDesign(body);
                setDefinition(body.Definition?.recordTypeRevisionUSID
                    ? body.Definition
                    : emptyDefinition(body.RecordTypeRevisionUSID));
                setShapeFields(body.ShapeFields ?? []);
                setDirty(false);
                setError(null);
                setLoading(false);
            })
            .catch((e: any) => {
                setError(e?.response?.data?.Cause ?? "This form could not be opened.");
                setLoading(false);
            });
    }, [usid]);

    useEffect(load, [load]);

    // The chooser, and the type list for inventing a field. Both are catalogue reference data, so
    // they are fetched once rather than per interaction.
    useEffect(() => {
        Axios({method: "GET", url: "t1/v1/form_shapes"})
            .then((r: any) => setShapes((r.data?.Shapes ?? []).filter((s: Shape) => s.CanCapture)))
            .catch(() => setShapes([]));
        Axios({method: "GET", url: "t1/v1/form_shapes", params: {DataTypes: true}})
            .then((r: any) => {
                // THE CATALOGUE ANSWERS WITH TYPE REVISIONS -- ListOfDataTypeRevision, each row the
                // revision in force with the type's name and kind joined on -- and a revision USID is
                // exactly what an invented field binds to. This read the list under the wrong tag
                // from the day it was written, so New field never offered a type (found 2026-10-29,
                // opening the designer on a record at Peak).
                const message = r.data?.PlatformMessage ?? {};
                const raw = message.ListOfDataTypeRevision?.DataTypeRevision ?? message.ListOfDataType?.DataType ?? [];
                const list = (Array.isArray(raw) ? raw : [raw]).filter(Boolean).map((t: any) => ({
                    USID: t.USID, Name: t.TypeName ?? t.Name, Kind: t.Kind, Code: t.TypeCode ?? t.Code,
                }));
                list.sort((a: any, b: any) => String(a.Name ?? "").localeCompare(String(b.Name ?? "")));
                setDataTypes(list);
            })
            .catch(() => setDataTypes([]));
    }, []);

    const change = (next: FormDefinition) => { setDefinition(next); setDirty(true); };

    const chooseShape = (revision: string) => {
        change({...emptyDefinition(revision)});
        Axios({method: "GET", url: "t1/v1/form_shapes", params: {CompositeRevisionUSID: revision}})
            .then((r: any) => setShapeFields(r.data?.Fields ?? []))
            .catch(() => setShapeFields([]));
    };

    const save = () => {
        setSaving(true);
        Axios({method: "PUT", url: "t1/v1/form_design",
               data: {FormRevisionUSID: usid, Definition: definition}})
            .then((response: any) => {
                // TAKE THE SERVER'S VERSION BACK, not just a success flag. It rewrote every declared
                // field from the shape, so the canvas would otherwise go on showing what was sent.
                setDefinition(response.data.Definition);
                setDirty(false);
                setError(null);
                setSaving(false);
            })
            .catch((e: any) => {
                setError(e?.response?.data?.Cause ?? "This form could not be saved.");
                setSaving(false);
            });
    };

    const act = (action: string) => {
        setSaving(true);
        Axios({method: "PUT", url: "t1/v1/object_revisions",
               data: {PlatformMessage: {"-idoc": IDOC, ObjectRevision: {USID: usid, LifecycleAction: action}}},
               headers: {"idoc-request": IDOC}})
            .then(() => { setSaving(false); load(); })
            .catch((e: any) => {
                setError(e?.response?.data?.Cause ?? `This form could not be ${action.toLowerCase()}d.`);
                setSaving(false);
            });
    };

    useEffect(() => {
        const shape = definition.recordTypeRevisionUSID;
        if (!shape) return;
        Axios({method: "POST", url: "t1/v1/object_form_definition",
               data: {CompositeRevisionUSID: shape}})
            .then((r: any) => {
                const byCode: Record<string, any[]> = {};
                (r.data?.Fields ?? []).forEach((f: any) => { byCode[f.Code] = f.Options ?? []; });
                setFieldOptions(byCode);
            })
            .catch(() => setFieldOptions({}));
    }, [definition.recordTypeRevisionUSID]);

    const palette = useMemo(() => unplaced(definition, shapeFields), [definition, shapeFields]);
    // Where a CLICK on the palette lands. Dragging carries its own section; clicking has to be
    // told, and telling it "the first section" meant a two-section form could only be filled by
    // dragging. See targetSectionFor.
    const target = targetSectionFor(definition, selection);
    const targetTitle = (definition.sections ?? []).find(s => s.key === target)?.title ?? target;
    const atRisk = hiddenButRequiredCodes(definition);

    // The blue bar names the SECTION and is the one part of this page that does not depend on which
    // form is open -- so it wraps the loading and not-found states too, rather than appearing only
    // once a design has loaded and making the page look like it changed identity.
    const page = (content: React.ReactNode) => (
        <div style={{height: "calc(100vh - 64px)", display: "flex", flexDirection: "column"}}>
            <PageHeader title="Form Designer"/>
            {content}
        </div>
    );

    if (loading) {
        return page(<div style={{padding: 40}}><CircularProgress size={22}/></div>);
    }

    if (!design) {
        return page(<Typography style={{padding: 40, fontSize: 14, color: "#B3261E"}}>{error}</Typography>);
    }

    return page(
        <div style={{padding: "20px 28px", flex: 1, minHeight: 0, display: "flex",
                     flexDirection: "column"}}>
            {/* Header */}
            <div style={{display: "flex", alignItems: "center", gap: 14, marginBottom: 6}}>
                <ArrowBack fontSize="small" style={{cursor: "pointer", color: Grey7}}
                           onClick={() => navigate("/forms")}/>
                <Typography style={{fontSize: 20, fontWeight: 500, color: Grey17}}>{design.Name}</Typography>
                {statusPill(design.Status)}
                <Typography style={{fontSize: 13, color: Grey7}}>Rev {design.Revision}</Typography>
                <div style={{flex: 1}}/>
                {/* The guide lived on the old bespoke list page; it belongs where the work happens. */}
                <CCButton className={css_buttons.whiteStandardButton}
                          onClick={() => window.open("/docs/form-designer.html", "cresFormDesignerDocs",
                                                     "width=1200,height=900,scrollbars=yes,resizable=yes")}>
                    Guide
                </CCButton>
                {dirty && <Typography style={{fontSize: 13, color: "#B26500"}}>Unsaved changes</Typography>}
                {(["design", "preview", "links"] as const).map(v => (
                    <CCButton key={v}
                              className={view === v ? css_buttons.blueStandardButton
                                                    : css_buttons.whiteStandardButton}
                              onClick={() => setView(v)}>
                        {v === "design" ? "Design" : v === "preview" ? "Preview" : "Offered on"}
                    </CCButton>
                ))}
                {editable && (
                    <CCButton className={css_buttons.blueStandardButton} disabled={saving || !dirty}
                              onClick={save}>{saving ? "Saving…" : "Save"}</CCButton>
                )}
                {editable && (
                    <CCButton className={css_buttons.whiteStandardButton} disabled={saving || dirty}
                              onClick={() => act("Release")}>Release</CCButton>
                )}
                {design.Status === "RELEASED" && (
                    <CCButton className={css_buttons.whiteStandardButton} disabled={saving}
                              onClick={() => act("Revise")}>Revise</CCButton>
                )}
            </div>

            {error && <Typography style={{fontSize: 13, color: "#B3261E", marginBottom: 8}}>{error}</Typography>}

            {/* The shape is chosen once. Locked afterwards: changing it would invalidate every
                placement in one click, and the codes mean different things in a different shape. */}
            {!definition.recordTypeRevisionUSID ? (
                <div style={{maxWidth: 460, marginTop: 24}}>
                    <Typography style={{fontSize: 14, color: Grey17, marginBottom: 10}}>
                        Which shape does this form present?
                    </Typography>
                    <TextField select fullWidth size="small" label="Shape" value=""
                               disabled={!editable}
                               onChange={e => chooseShape(e.target.value)}
                               helperText="The questions a record of this kind may hold. It cannot be changed later.">
                        {shapes.map(s => (
                            <MenuItem key={s.RecordTypeRevisionUSID} value={s.RecordTypeRevisionUSID}>
                                {s.Name} · rev {s.Revision}
                            </MenuItem>
                        ))}
                    </TextField>
                </div>
            ) : view === "links" ? (
                <FormLinks formUSID={design.FormUSID ?? null} status={design.Status}/>
            ) : view === "preview" ? (
                <div style={{overflowY: "auto", paddingTop: 12, maxWidth: 900}}>
                    {/* The real renderer, over the SHAPE — there is no record yet. It draws controls
                        and options for real, which the canvas deliberately does not. */}
                    <FormRenderer compositeRevisionUSID={definition.recordTypeRevisionUSID!}
                                  readOnly value={{}}/>
                </div>
            ) : (
                <>
                    {atRisk.length > 0 && (
                        <Typography style={{fontSize: 12, color: "#B3261E", marginBottom: 8}}>
                            {atRisk.length === 1
                                ? `1 question can be required while hidden.`
                                : `${atRisk.length} questions can be required while hidden.`}{" "}
                            The save refuses for a question nobody can see. Select
                            {atRisk.length === 1 ? " it" : " them"} to fix the rule.
                        </Typography>
                    )}
                    <div style={{display: "flex", flex: 1, minHeight: 0, gap: 4}}>
                        <Palette
                            fields={palette}
                            dataTypes={dataTypes}
                            disabled={!editable}
                            targetSection={targetTitle}
                            onPlace={f => change(placeField(definition, f, target, shapeFields))}
                            onInvent={(code, name, type) =>
                                change(inventField(definition, code, name, type, target))}
                            onAddDisplay={kind => change(addDisplay(definition, kind, target))}
                        />
                        <Canvas
                            definition={definition}
                            selection={selection}
                            disabled={!editable}
                            openTab={openTab ?? (definition.tabs ?? [])[0]?.key ?? null}
                            onOpenTab={setOpenTab}
                            onAddTab={() => {
                                const title = `Tab ${(definition.tabs ?? []).length + 1}`;
                                const key = sectionKeyFor(title, (definition.tabs ?? []) as any);
                                change(addTab(definition, key, title));
                                setOpenTab(key);
                                setSelection({kind: "tab", key});
                            }}
                            onSelect={setSelection}
                            onAddSection={() => {
                                const title = `Section ${(definition.sections ?? []).length + 1}`;
                                const key = sectionKeyFor(title, definition.sections ?? []);
                                const tab = openTab ?? (definition.tabs ?? [])[0]?.key ?? null;
                                // Into the tab being looked at. Adding a section while a tab is open
                                // and having it appear somewhere else is the same surprise the
                                // palette used to spring by always using the first section.
                                change(renameSection(addSection(definition, key, title), key, {tab}));
                                setSelection({kind: "section", key});
                            }}
                            onRemoveSection={key => {
                                change(removeSection(definition, key));
                                setSelection(null);
                            }}
                            onRemoveField={code => {
                                change(removeField(definition, code));
                                setSelection(null);
                            }}
                            onRemoveDisplay={index => {
                                change(removeDisplay(definition, index));
                                setSelection(null);
                            }}
                            onMove={(moving, section, before) =>
                                change(moveInto(definition, moving, section, before))}
                            onDropField={(code, section, before) => {
                                const field = shapeFields.find(f => f.Code === code);
                                if (!field) return;
                                const placed = placeField(definition, field, section, shapeFields);
                                const item = itemsIn(placed, section).find(
                                    i => i.kind === "field" && (i as any).placement.code === code);
                                change(item ? moveInto(placed, item as CanvasItem, section, before) : placed);
                            }}
                            onDropDisplay={(kind, section, before) => {
                                const added = addDisplay(definition, kind as DisplayItem["kind"], section);
                                const items = itemsIn(added, section);
                                const item = items[items.length - 1];
                                change(item ? moveInto(added, item, section, before) : added);
                            }}
                        />
                        <Inspector
                            definition={definition}
                            selection={selection}
                            disabled={!editable}
                            shapeFields={shapeFields}
                            fieldOptions={fieldOptions}
                            onPlacement={(code, patch: Partial<Placement>) =>
                                change(updatePlacement(definition, code, patch))}
                            onDisplay={(index, patch: Partial<DisplayItem>) =>
                                change(updateDisplay(definition, index, patch))}
                            onSection={(key, patch: Partial<Section>) =>
                                change(renameSection(definition, key, patch))}
                            onTab={(key, patch: Partial<Tab>) =>
                                change(renameTab(definition, key, patch))}
                            onRemoveTab={key => {
                                change(removeTab(definition, key));
                                setOpenTab(null);
                                setSelection(null);
                            }}
                        />
                    </div>
                </>
            )}
        </div>
    );
}
